/**
 * Order Details Page
 * Modern, professional design with improved UX
 */

import { useParams, useNavigate } from 'react-router-dom'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import { useOrderDetails, useInvalidateOrderDetails } from '@/hooks/useOrderDetails'
import {
  useCancelOrder,
  getStatusColor,
  getPriorityColor,
  formatOrderAmount,
  formatOrderDate,
  formatOrderDateTime,
  type OrderStatus,
  type OrderPriority,
} from '@/hooks/useOrders'
import { useAppointmentActions } from '@/hooks/useAppointmentActions'
import { useServiceActions } from '@/hooks/useServiceActions'
import {
  ArrowLeft,
  RefreshCw,
  Calendar,
  Clock,
  MapPin,
  Phone,
  Mail,
  User,
  Users,
  Building2,
  FileText,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  Package,
  Receipt,
  Activity,
  Timer,
  DollarSign,
  Clipboard,
  ExternalLink,
  ChevronRight,
  CalendarClock,
  CircleDot,
  Play,
  RotateCcw,
  Ban,
  MessageSquare,
  Radio,
} from 'lucide-react'
import type { OrderDetails, Appointment, Service } from '@/api/orderDetails'
import { useLocalization } from '@/contexts/LocalizationContext'
import { formatOrderRequirements } from '@/utils/orderRequirements'
import { CancellationReasonDialog } from '@/components/order/CancellationReasonDialog'
import { useProfileSwitch } from '@/contexts/ProfileSwitchContext'
import {
  ORDER_CANCEL_REASONS,
  APPOINTMENT_CANCEL_REASONS,
  SERVICE_CANCEL_REASONS,
} from '@/constants/cancellationReasons'
import { OrderSurveysTab } from '@/components/order/survey/OrderSurveysTab'
import { BeneficiaryHealthProfileCard } from '@/components/order/BeneficiaryHealthProfileCard'
import { OrderProviderCard } from '@/components/order/OrderProviderCard'
import { ServiceLiveTrackingCard } from '@/components/order/ServiceLiveTrackingCard'
import { isTerminalOrderStatus, useOrderTrackingFlags } from '@/hooks/useLiveTracking'
import { GeocodedLocationLink } from '@/components/shared/GeocodedLocationLink'

type TabId = 'overview' | 'services' | 'appointments' | 'quotation' | 'survey'

const TABS: { id: TabId; labelKey: string; icon: React.ReactNode }[] = [
  { id: 'overview', labelKey: 'orderDetails.overview', icon: <Clipboard className="h-4 w-4" /> },
  { id: 'services', labelKey: 'orderDetails.services', icon: <Package className="h-4 w-4" /> },
  { id: 'appointments', labelKey: 'orderDetails.appointments', icon: <CalendarClock className="h-4 w-4" /> },
  { id: 'quotation', labelKey: 'orderDetails.quotation', icon: <Receipt className="h-4 w-4" /> },
  { id: 'survey', labelKey: 'orderDetails.survey', icon: <MessageSquare className="h-4 w-4" /> },
]

/** Map API status strings to translation keys so Overview/Services/Appointments show in current locale */
function translateAppointmentStatus(t: (key: string) => string, status: string | null | undefined): string {
  if (!status) return ''
  const normalized = status.toLowerCase().replace(/\s+/g, '_').replace(/-/g, '_')
  const key = `status.${normalized}` as const
  const translated = t(key)
  return translated !== key ? translated : status
}

function formatLocationLabel(
  address: string | null | undefined,
  city: string | null | undefined
): string {
  const parts = [address?.trim(), city?.trim()].filter(Boolean) as string[]
  return parts.join(', ')
}

function isZeroAmount(amount: string | number | null | undefined): boolean {
  if (amount === null || amount === undefined || amount === '') return true
  const parsed = typeof amount === 'number' ? amount : Number.parseFloat(amount)
  return Number.isFinite(parsed) && parsed === 0
}

function getServiceDisplayPrice(service: Service, order: OrderDetails): { amount: string; currency: string } {
  if (!isZeroAmount(service.basePrice)) {
    return {
      amount: service.basePrice,
      currency: service.currency,
    }
  }

  return {
    amount: order.quotation?.subtotal || order.subtotal || service.basePrice,
    currency: order.quotation?.currency || order.currency || service.currency,
  }
}

export function OrderDetailsPage() {
  const { t, locale } = useLocalization()
  const { isBusinessClientMode } = useProfileSwitch()
  const { orderId } = useParams<{ orderId: string }>()
  const navigate = useNavigate()
  const [activeTab, setActiveTab] = useState<TabId>('overview')

  const { order, isLoading, isError, error, refetch } = useOrderDetails(orderId)
  const invalidateOrderDetails = useInvalidateOrderDetails()

  // Polled separately from the order itself: this is what re-arms the tracking card
  // when a provider starts a visit while the page is already open.
  const { flags: trackingFlags } = useOrderTrackingFlags(
    orderId,
    !isTerminalOrderStatus(order?.status)
  )
  const trackedServiceIds = useMemo(
    () =>
      new Set(
        (trackingFlags ?? []).filter((f) => f.liveTrackingActive).map((f) => f.id)
      ),
    [trackingFlags]
  )

  // Order-level mutation hooks
  const cancelOrderMutation = useCancelOrder()

  // Appointment & Service action hooks
  const { cancelAppointment, cancelMutation: cancelAppointmentMutation } = useAppointmentActions()
  const { cancelService, cancelMutation: cancelServiceMutation } = useServiceActions()

  // Dialog state
  const [cancelOrderOpen, setCancelOrderOpen] = useState(false)
  const [cancelAppointmentOpen, setCancelAppointmentOpen] = useState(false)
  const [cancelAppointmentId, setCancelAppointmentId] = useState<string | null>(null)
  const [cancelServiceOpen, setCancelServiceOpen] = useState(false)
  const [cancelServiceId, setCancelServiceId] = useState<string | null>(null)

  const handleCancelOrder = async (data: { reason: string }) => {
    if (!orderId) return
    await cancelOrderMutation.mutateAsync({ orderId, reason: data.reason })
    invalidateOrderDetails(orderId)
    setCancelOrderOpen(false)
  }

  const handleCancelAppointment = async (data: { reason: string }) => {
    if (!cancelAppointmentId) return
    await cancelAppointment(cancelAppointmentId, data.reason)
    if (orderId) invalidateOrderDetails(orderId)
    setCancelAppointmentOpen(false)
    setCancelAppointmentId(null)
  }

  const handleCancelService = async (data: { reason: string }) => {
    if (!cancelServiceId) return
    await cancelService(cancelServiceId, data.reason)
    if (orderId) invalidateOrderDetails(orderId)
    setCancelServiceOpen(false)
    setCancelServiceId(null)
  }

  const openCancelAppointment = (appointmentId: string) => {
    setCancelAppointmentId(appointmentId)
    setCancelAppointmentOpen(true)
  }

  const openCancelService = (serviceId: string) => {
    setCancelServiceId(serviceId)
    setCancelServiceOpen(true)
  }

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || (e.key === 'Backspace' && !(e.target as HTMLElement).closest('input, textarea'))) {
        e.preventDefault()
        navigate('/orders')
        return
      }

      const tabIndex = parseInt(e.key, 10) - 1
      if (tabIndex >= 0 && tabIndex < TABS.length) {
        e.preventDefault()
        setActiveTab(TABS[tabIndex].id)
      }

      if ((e.key === 'r' || e.key === 'R') && !e.metaKey && !e.ctrlKey) {
        e.preventDefault()
        refetch()
      }
    }

    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [navigate, refetch])

  if (isLoading) {
    return <OrderDetailsLoading />
  }

  if (isError || !order) {
    return <OrderDetailsError error={error} onRetry={() => refetch()} onBack={() => navigate('/orders')} />
  }

  const normalizedStatus = (order.status?.toLowerCase() || 'draft') as OrderStatus
  const normalizedPriority = (order.priority?.toLowerCase() || 'normal') as OrderPriority

  return (
    <div className="min-h-screen bg-gray-50/50">
      {/* Page Header */}
      <div className="bg-white border-b border-gray-200">
        <div className="px-4 sm:px-6 py-4 sm:py-5">
          {/* Navigation Row */}
          <div className="flex items-center gap-3 mb-3 sm:mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/orders')}
              className="gap-2 text-gray-600 hover:text-gray-900 -ms-2"
            >
              <ArrowLeft className="h-4 w-4" />
              <span className="hidden sm:inline">{t('orderDetails.backToOrders')}</span>
              <span className="sm:hidden">{t('common.back')}</span>
            </Button>
            <kbd className="hidden sm:inline rounded border border-gray-200 bg-gray-50 px-2 py-0.5 text-xs text-gray-500">
              Esc
            </kbd>
          </div>

          {/* Title Row */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-3 sm:gap-4">
            <div className="flex items-start gap-3 sm:gap-4">
              {/* Order Icon */}
              <div className="hidden sm:flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-brand-primary to-brand-secondary text-white shadow-lg flex-shrink-0">
                <Package className="h-7 w-7" />
              </div>
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2 sm:gap-3">
                  <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{order.orderNumber}</h1>
                  <Badge className={`${getStatusColor(normalizedStatus)} border-0`}>
                    {t(`status.${normalizedStatus}`) || order.status}
                  </Badge>
                  <Badge className={`${getPriorityColor(normalizedPriority)} border-0`}>
                    {t(`status.${normalizedPriority}`) || order.priority}
                  </Badge>
                  {order.isOverdue && (
                    <Badge className="bg-red-600 text-white border-0">
                      <AlertCircle className="h-3 w-3 me-1" />
                      {t('common.overdue')}
                    </Badge>
                  )}
                </div>
                <p className="mt-1 text-xs sm:text-sm text-gray-500 truncate">
                  {t('orderDetails.createdOn', {
                    date: formatOrderDateTime(order.createdAt, locale),
                    client: order.clientName || '',
                  })}
                </p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="mt-4 sm:mt-6 flex gap-1 -mb-px overflow-x-auto -mx-4 sm:-mx-6 px-4 sm:px-6 scrollbar-hide">
            {TABS.map((tab, index) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`relative flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium whitespace-nowrap transition-all rounded-t-lg ${
                  activeTab === tab.id
                    ? 'bg-gray-50 text-brand-primary border border-b-0 border-gray-200'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50/50'
                }`}
              >
                {tab.icon}
                {t(tab.labelKey)}
                <kbd className="hidden lg:inline rounded border border-gray-200 bg-white px-1.5 py-0.5 text-xs text-gray-400">
                  {index + 1}
                </kbd>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Tab Content - key ensures re-render when locale changes so Arabic shows correctly */}
      <div key={locale} className="px-4 sm:px-6 py-4 sm:py-6">
        {activeTab === 'overview' && (
          <OverviewTab
            order={order}
            locale={locale}
            isBusinessClientMode={isBusinessClientMode}
            trackedServiceIds={trackedServiceIds}
            onCancelOrder={() => setCancelOrderOpen(true)}
          />
        )}
        {activeTab === 'services' && (
          <ServicesTab
            order={order}
            locale={locale}
            trackedServiceIds={trackedServiceIds}
            onTrackService={() => setActiveTab('overview')}
            onCancelService={openCancelService}
          />
        )}
        {activeTab === 'appointments' && (
          <AppointmentsTab order={order} locale={locale} onCancelAppointment={openCancelAppointment} />
        )}
        {activeTab === 'quotation' && (
          <QuotationTab
            order={order}
            locale={locale}
            isBusinessClientMode={isBusinessClientMode}
          />
        )}
        {activeTab === 'survey' && <OrderSurveysTab order={order} locale={locale} />}
      </div>

      {/* Cancel Order Dialog */}
      <CancellationReasonDialog
        open={cancelOrderOpen}
        onOpenChange={setCancelOrderOpen}
        title={t('cancellation.cancelOrderTitle')}
        description={t('cancellation.cancelOrderDescription')}
        reasons={ORDER_CANCEL_REASONS}
        confirmLabel={t('cancellation.cancelOrder')}
        isLoading={cancelOrderMutation.isPending}
        onConfirm={handleCancelOrder}
      />

      {/* Cancel Appointment Dialog */}
      <CancellationReasonDialog
        open={cancelAppointmentOpen}
        onOpenChange={(open) => {
          setCancelAppointmentOpen(open)
          if (!open) setCancelAppointmentId(null)
        }}
        title={t('cancellation.cancelAppointmentTitle')}
        description={t('cancellation.cancelAppointmentDescription')}
        reasons={APPOINTMENT_CANCEL_REASONS}
        confirmLabel={t('cancellation.cancelAppointment')}
        isLoading={cancelAppointmentMutation.isPending}
        onConfirm={handleCancelAppointment}
      />

      {/* Cancel Service Dialog */}
      <CancellationReasonDialog
        open={cancelServiceOpen}
        onOpenChange={(open) => {
          setCancelServiceOpen(open)
          if (!open) setCancelServiceId(null)
        }}
        title={t('cancellation.cancelServiceTitle')}
        description={t('cancellation.cancelServiceDescription')}
        reasons={SERVICE_CANCEL_REASONS}
        confirmLabel={t('cancellation.cancelService')}
        isLoading={cancelServiceMutation.isPending}
        onConfirm={handleCancelService}
      />
    </div>
  )
}

// ============================================================================
// Quick Stat Component
// ============================================================================


// ============================================================================
// Loading State
// ============================================================================

function OrderDetailsLoading() {
  return (
    <div className="min-h-screen bg-gray-50/50">
      <div className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4 sm:py-5">
        <Skeleton className="h-6 w-32 mb-4" />
        <div className="flex items-start gap-3 sm:gap-4">
          <Skeleton className="hidden sm:block h-14 w-14 rounded-xl" />
          <div className="flex-1 min-w-0">
            <Skeleton className="h-7 sm:h-8 w-48 sm:w-64 mb-2" />
            <Skeleton className="h-4 w-36 sm:w-48" />
          </div>
        </div>
        <div className="mt-4 sm:mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-16 rounded-lg" />
          ))}
        </div>
        <div className="mt-4 sm:mt-6 flex gap-2 overflow-x-auto">
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} className="h-10 w-24 sm:w-28 rounded-lg flex-shrink-0" />
          ))}
        </div>
      </div>
      <div className="px-4 sm:px-6 py-4 sm:py-6">
        <div className="grid gap-4 sm:gap-6 lg:grid-cols-3">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Skeleton key={i} className="h-48 rounded-xl" />
          ))}
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Error State
// ============================================================================

function OrderDetailsError({
  error,
  onRetry,
  onBack,
}: {
  error: Error | null
  onRetry: () => void
  onBack: () => void
}) {
  const { t } = useLocalization()
  return (
    <div className="min-h-screen bg-gray-50/50 flex items-center justify-center p-6">
      <div className="text-center max-w-md">
        <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-red-100">
          <AlertCircle className="h-10 w-10 text-red-600" />
        </div>
        <h2 className="mb-2 text-2xl font-bold text-gray-900">{t('orderDetails.failedToLoad')}</h2>
        <p className="mb-6 text-gray-600">{error?.message || t('orderDetails.orderNotFound')}</p>
        <div className="flex justify-center gap-3">
          <Button variant="outline" onClick={onBack} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            {t('orderDetails.backToOrders')}
          </Button>
          <Button onClick={onRetry} className="gap-2">
            <RefreshCw className="h-4 w-4" />
            {t('orderDetails.tryAgain')}
          </Button>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Overview Tab
// ============================================================================

function OverviewTab({
  order,
  locale,
  isBusinessClientMode,
  trackedServiceIds,
  onCancelOrder,
}: {
  order: OrderDetails
  locale: 'en' | 'ar'
  isBusinessClientMode: boolean
  trackedServiceIds: Set<string>
  onCancelOrder: () => void
}) {
  const { t } = useLocalization()
  const trackedServices = order.services.filter((s) => trackedServiceIds.has(s.id))

  return (
    <div className="space-y-6">
      {/* Live tracking — only while a visit is actually in progress and tracked */}
      {trackedServices.map((service) => (
        <ServiceLiveTrackingCard key={service.id} service={service} />
      ))}

      {/* Parties Section */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Client Card */}
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50/50 px-5 py-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-blue-100 text-blue-600">
              <User className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{t('orderDetails.client')}</h3>
              <p className="text-xs text-gray-500">{t('orderDetails.customerInformation')}</p>
            </div>
          </div>
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-brand-secondary to-brand-primary text-white font-bold text-lg">
                {order.clientName?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div>
                <p className="font-semibold text-gray-900">{order.clientName}</p>
                {order.organizationName && (
                  <p className="text-sm text-gray-500 flex items-center gap-1">
                    <Building2 className="h-3 w-3" />
                    {order.organizationName}
                  </p>
                )}
              </div>
            </div>
            {/* <div className="grid gap-2 pt-2 border-t border-gray-100">
              {order.clientPhone && (
                <a
                  href={`tel:${order.clientPhone}`}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <Phone className="h-4 w-4 text-gray-400 group-hover:text-brand-primary" />
                  <span className="text-sm text-gray-700 group-hover:text-brand-primary">{order.clientPhone}</span>
                </a>
              )}
              {order.clientEmail && (
                <a
                  href={`mailto:${order.clientEmail}`}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <Mail className="h-4 w-4 text-gray-400 group-hover:text-brand-primary" />
                  <span className="text-sm text-gray-700 group-hover:text-brand-primary">{order.clientEmail}</span>
                </a>
              )}
            </div> */}
          </div>
        </div>

        {/* Beneficiary Card */}
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50/50 px-5 py-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-primary/10 text-brand-primary">
              <Users className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{t('orderDetails.beneficiary')}</h3>
              <p className="text-xs text-gray-500">{t('orderDetails.serviceRecipient')}</p>
            </div>
          </div>
          <div className="p-5 space-y-4">
            <div className="flex items-center gap-3">
              <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-brand-primary to-brand-secondary text-white font-bold text-lg">
                {order.beneficiaryName?.charAt(0)?.toUpperCase() || '?'}
              </div>
              <div>
                <p className="font-semibold text-gray-900">{order.beneficiaryName || t('orderDetails.sameAsClient')}</p>
                {order.beneficiaryRelationship && (
                  <p className="text-sm text-gray-500">{order.beneficiaryRelationship}</p>
                )}
              </div>
            </div>
            {/* {order.beneficiaryPhone && (
              <div className="pt-2 border-t border-gray-100">
                <a
                  href={`tel:${order.beneficiaryPhone}`}
                  className="flex items-center gap-3 px-3 py-2 rounded-lg hover:bg-gray-50 transition-colors group"
                >
                  <Phone className="h-4 w-4 text-gray-400 group-hover:text-brand-primary" />
                  <span className="text-sm text-gray-700 group-hover:text-brand-primary">{order.beneficiaryPhone}</span>
                </a>
              </div>
            )} */}
          </div>
        </div>
      </div>

      {/* Assigned Providers */}
      <OrderProviderCard services={order.services} />

      {/* Beneficiary Health & Care Profile */}
      <BeneficiaryHealthProfileCard surveyData={order.beneficiaryProfile?.surveyData} />

      {/* Order Info & Timeline */}
      <div className="grid gap-6 lg:grid-cols-3">
        {/* Order Details Card */}
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50/50 px-5 py-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-brand-primary/10 text-brand-primary">
              <FileText className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{t('orderDetails.orderDetails')}</h3>
              <p className="text-xs text-gray-500">{t('orderDetails.serviceInformation')}</p>
            </div>
          </div>
          <div className="p-5 space-y-3">
            <DetailRow label={t('orderDetails.orderNumber')} value={order.orderNumber} />
            <DetailRow label={t('orderDetails.template')} value={order.templateName || '-'} />
            <DetailRow label={t('orderDetails.category')} value={order.categoryName || '-'} />
            {order.organizationName && (
              <DetailRow label={t('orderDetails.organization')} value={order.organizationName} />
            )}
          </div>
        </div>

        {/* Timeline Card */}
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50/50 px-5 py-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-orange-100 text-orange-600">
              <Clock className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{t('orderDetails.timeline')}</h3>
              <p className="text-xs text-gray-500">{t('orderDetails.orderHistory')}</p>
            </div>
          </div>
          <div className="p-5">
            <div className="relative space-y-4">
              <TimelineItem
                label={t('orderDetails.created')}
                date={order.createdAt}
                locale={locale}
                isCompleted
                isFirst
              />
              {order.submittedAt && (
                <TimelineItem
                  label={t('orderDetails.submitted')}
                  date={order.submittedAt}
                  locale={locale}
                  isCompleted
                />
              )}
              {order.confirmedAt && (
                <TimelineItem
                  label={t('orderDetails.confirmed')}
                  date={order.confirmedAt}
                  locale={locale}
                  isCompleted
                />
              )}
              {order.completedAt && (
                <TimelineItem
                  label={t('orderDetails.completed')}
                  date={order.completedAt}
                  locale={locale}
                  isCompleted
                  isLast
                />
              )}
              {order.deadline && !order.completedAt && (
                <TimelineItem
                  label={t('dashboard.deadline')}
                  date={order.deadline}
                  locale={locale}
                  isOverdue={order.isOverdue}
                  isLast
                />
              )}
            </div>
          </div>
        </div>

        {/* Pricing Card */}
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50/50 px-5 py-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-green-100 text-green-600">
              <DollarSign className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{t('orderDetails.pricing')}</h3>
              <p className="text-xs text-gray-500">{t('orderDetails.financialDetails')}</p>
            </div>
          </div>
          <div className="p-5 space-y-3">
            {isBusinessClientMode ? (
              <>
                <DetailRow
                  label={t('orderDetails.subtotal')}
                  value={formatOrderAmount(order.subtotal, order.currency, locale)}
                />
                <DetailRow
                  label={t('orderDetails.vat')}
                  value={formatOrderAmount(order.vatAmount, order.currency, locale)}
                />
                <div className="border-t border-gray-100 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-900">{t('orderDetails.totalAmount')}</span>
                    <span className="text-xl font-bold text-green-600">
                      {formatOrderAmount(order.totalAmount, order.currency, locale)}
                    </span>
                  </div>
                </div>
              </>
            ) : (
              <>
                {/* <DetailRow
                  label={t('orderDetails.grossServiceFee')}
                  value={
                    order.providerEarningsGrossAmount !== null
                      ? formatOrderAmount(order.providerEarningsGrossAmount, order.providerEarningsCurrency || order.currency, locale)
                      : t('common.noData')
                  }
                /> */}
                <DetailRow
                  label={t('orderDetails.netServiceFee')}
                  value={
                    order.providerEarningsNet !== null
                      ? formatOrderAmount(order.providerEarningsNet, order.providerEarningsCurrency || order.currency, locale)
                      : t('common.noData')
                  }
                />
                {/* <DetailRow
                  label={t('orderDetails.taxVat')}
                  value={
                    order.providerEarningsTax !== null
                      ? formatOrderAmount(order.providerEarningsTax, order.providerEarningsCurrency || order.currency, locale)
                      : t('common.noData')
                  }
                /> */}
                <div className="border-t border-gray-100 pt-3">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold text-gray-900">{t('orderDetails.providerEarningsTotal')}</span>
                    <span className="text-xl font-bold text-green-600">
                      {order.providerEarningsTotal !== null
                        ? formatOrderAmount(order.providerEarningsTotal, order.providerEarningsCurrency || order.currency, locale)
                        : t('common.noData')}
                    </span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Progress & Location */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Progress Card */}
        <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
          <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50/50 px-5 py-4">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-teal-100 text-teal-600">
              <Activity className="h-5 w-5" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">{t('orderDetails.progress')}</h3>
              <p className="text-xs text-gray-500">{t('orderDetails.serviceCompletion')}</p>
            </div>
          </div>
          <div className="p-5">
            <div className="mb-4">
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium text-gray-700">{t('orderDetails.overallCompletion')}</span>
                <span className="text-sm font-bold text-brand-primary">{order.servicesProgressPercentage}%</span>
              </div>
              <div className="h-3 overflow-hidden rounded-full bg-gray-100">
                <div
                  className="h-full bg-gradient-to-r from-brand-primary to-brand-secondary transition-all duration-500"
                  style={{ width: `${order.servicesProgressPercentage}%` }}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4 pt-4 border-t border-gray-100">
              <div className="text-center p-3 rounded-lg bg-gray-50">
                <p className="text-2xl font-bold text-gray-900">{order.completedServicesCount}</p>
                <p className="text-xs text-gray-500">{t('orderDetails.completed')}</p>
              </div>
              <div className="text-center p-3 rounded-lg bg-gray-50">
                <p className="text-2xl font-bold text-gray-900">{order.totalServices - order.completedServicesCount}</p>
                <p className="text-xs text-gray-500">{t('orderDetails.remaining')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Location Card */}
        {order.primaryLocationAddress && (
          <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
            <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50/50 px-5 py-4">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-red-100 text-red-600">
                <MapPin className="h-5 w-5" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900">{t('orderDetails.location')}</h3>
                <p className="text-xs text-gray-500">{t('orderDetails.serviceAddress')}</p>
              </div>
            </div>
            <div className="p-5">
              <GeocodedLocationLink
                location={formatLocationLabel(
                  order.primaryLocationAddress,
                  order.primaryLocationCity
                )}
                href={order.primaryLocationGoogleMapsLink}
                className="text-base font-medium"
              />
            </div>
          </div>
        )}
      </div>

      {/* Notes Section */}
      {(order.specialInstructions || order.requirements) && (
        <div className="grid gap-6 lg:grid-cols-2">
          {order.specialInstructions && (
            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
              <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50/50 px-5 py-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-yellow-100 text-yellow-600">
                  <AlertCircle className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{t('orderDetails.specialInstructions')}</h3>
                  <p className="text-xs text-gray-500">{t('orderDetails.importantNotes')}</p>
                </div>
              </div>
              <div className="p-5">
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {order.specialInstructions}
                </p>
              </div>
            </div>
          )}
          {order.requirements && (
            <div className="rounded-xl border border-gray-200 bg-white overflow-hidden">
              <div className="flex items-center gap-3 border-b border-gray-100 bg-gray-50/50 px-5 py-4">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-cyan-100 text-cyan-600">
                  <Clipboard className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{t('orderDetails.requirements')}</h3>
                  <p className="text-xs text-gray-500">{t('orderDetails.serviceRequirements')}</p>
                </div>
              </div>
              <div className="p-5">
                <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">
                  {formatOrderRequirements(order.requirements)}
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Action Buttons */}
      {(order.canCancel || order.canReschedule) && (
        <div className="flex flex-wrap gap-3 pt-4 border-t border-gray-200">
          {/* {order.canCancel && (
            <Button
              variant="outline"
              className="gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
              onClick={onCancelOrder}
            >
              <Ban className="h-4 w-4" />
              {t('cancellation.cancelOrder')}
            </Button>
          )} */}
          {order.canReschedule && (
            <Button variant="outline" className="gap-2">
              <RotateCcw className="h-4 w-4" />
              {t('orderDetails.rescheduleOrder')}
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Services Tab
// ============================================================================

function ServicesTab({
  order,
  locale,
  trackedServiceIds,
  onTrackService,
  onCancelService,
}: {
  order: OrderDetails
  locale: 'en' | 'ar'
  trackedServiceIds: Set<string>
  onTrackService: () => void
  onCancelService: (serviceId: string) => void
}) {
  const { t } = useLocalization()
  if (order.services.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
          <Package className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">{t('orderDetails.noServices')}</h3>
        <p className="mt-1 text-gray-500">{t('orderDetails.noServicesYet')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Summary Bar */}
      <div className="flex flex-wrap items-center gap-4 p-4 rounded-xl bg-white border border-gray-200">
        <div className="flex items-center gap-2">
          <Package className="h-5 w-5 text-gray-500" />
          <span className="font-medium text-gray-900">{t('orderDetails.servicesCount', { count: order.services.length })}</span>
        </div>
        <div className="h-4 w-px bg-gray-200" />
        <Badge className="bg-green-100 text-green-700 border-0">
          <CheckCircle className="h-3 w-3 me-1" />
          {t('orderDetails.completedCount', { count: order.completedServicesCount })}
        </Badge>
        <Badge className="bg-yellow-100 text-yellow-700 border-0">
          <Clock className="h-3 w-3 me-1" />
          {t('orderDetails.pendingCount', { count: order.services.length - order.completedServicesCount })}
        </Badge>
      </div>

      {/* Services Grid */}
      <div className="grid gap-4 md:grid-cols-2">
        {order.services.map((service, index) => {
          const serviceDisplayPrice = getServiceDisplayPrice(service, order)

          return (
            <div
              key={service.id}
              className="rounded-xl border border-gray-200 bg-white overflow-hidden hover:shadow-md transition-shadow"
            >
              <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/50 px-5 py-3">
                <div className="flex items-center gap-3">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-lg text-sm font-bold text-white ${
                    service.status === 'completed' ? 'bg-green-500' : service.status === 'cancelled' ? 'bg-red-500' : 'bg-brand-primary'
                  }`}>
                    {index + 1}
                  </div>
                  <span className="text-xs font-medium text-gray-500">{service.serviceCategory}</span>
                </div>
                <div className="flex items-center gap-2">
                  {trackedServiceIds.has(service.id) && (
                    <button
                      type="button"
                      onClick={onTrackService}
                      title={t('liveTracking.trackService')}
                      className="inline-flex items-center gap-1.5 rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 transition-colors hover:bg-green-200"
                    >
                      <Radio className="h-3 w-3" />
                      {t('liveTracking.live')}
                    </button>
                  )}
                  <Badge className={`border-0 ${
                    service.status === 'completed'
                      ? 'bg-green-100 text-green-700'
                      : service.status === 'cancelled'
                        ? 'bg-red-100 text-red-700'
                        : 'bg-yellow-100 text-yellow-700'
                  }`}>
                    {t(`status.${(service.status || '').toLowerCase().replace(/-/g, '_')}`) || service.status}
                  </Badge>
                  {service.isOverdue && (
                    <Badge className="bg-red-600 text-white border-0">{t('status.overdue')}</Badge>
                  )}
                </div>
              </div>
              <div className="p-5">
                <h3 className="font-semibold text-gray-900 mb-2">{service.serviceName}</h3>
                {service.serviceDescription && (
                  <p className="text-sm text-gray-500 mb-4 line-clamp-2">{service.serviceDescription}</p>
                )}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">{t('orderDetails.price')}</p>
                    <p className="font-semibold text-gray-900">
                      {formatOrderAmount(serviceDisplayPrice.amount, serviceDisplayPrice.currency, locale)}
                    </p>
                  </div>
                  {service.providerName && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">{t('orderDetails.provider')}</p>
                      <p className="font-medium text-gray-900 truncate">{service.providerName}</p>
                    </div>
                  )}
                  {service.appointmentScheduledStart && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">{t('orderDetails.scheduled')}</p>
                      <p className="font-medium text-gray-900">{formatOrderDateTime(service.appointmentScheduledStart, locale)}</p>
                    </div>
                  )}
                  {(service.locationAddress || service.locationCity) && (
                    <div>
                      <p className="text-xs text-gray-500 mb-1">{t('orderDetails.location')}</p>
                      <GeocodedLocationLink
                        location={formatLocationLabel(service.locationAddress, service.locationCity)}
                        href={service.locationGoogleMapsLink}
                        className="text-sm font-medium"
                        wrap
                      />
                    </div>
                  )}
                </div>
                {/* {service.canCancel && (
                <div className="flex gap-2 pt-4 border-t border-gray-100 mt-4">
                  <Button
                    size="sm"
                    variant="outline"
                    className="gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                    onClick={() => onCancelService(service.id)}
                  >
                    <XCircle className="h-3 w-3" />
                    {t('cancellation.cancelService')}
                  </Button>
                </div>
              )} */}
              </div>
            </div>
          )
        })}
      </div>
    </div>
  )
}

// ============================================================================
// Appointments Tab
// ============================================================================

function AppointmentsTab({ order, locale, onCancelAppointment }: { order: OrderDetails; locale: 'en' | 'ar'; onCancelAppointment: (appointmentId: string) => void }) {
  const { t } = useLocalization()
  if (order.appointments.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
          <Calendar className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">{t('orderDetails.noAppointments')}</h3>
        <p className="mt-1 text-gray-500">{t('orderDetails.noAppointmentsYet')}</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Summary Bar */}
      <div className="flex flex-wrap items-center gap-4 p-4 rounded-xl bg-white border border-gray-200">
        <div className="flex items-center gap-2">
          <Calendar className="h-5 w-5 text-gray-500" />
          <span className="font-medium text-gray-900">{t('orderDetails.appointmentsCount', { count: order.appointments.length })}</span>
        </div>
      </div>

      {/* Appointments List */}
      <div className="space-y-4">
        {order.appointments.map((appointment, index) => (
          <div
            key={appointment.id}
            className="rounded-xl border border-gray-200 bg-white overflow-hidden hover:shadow-md transition-shadow"
          >
            <div className="flex items-center justify-between border-b border-gray-100 bg-gray-50/50 px-5 py-3">
              <div className="flex items-center gap-3">
                <div className={`flex h-10 w-10 items-center justify-center rounded-xl text-white ${
                  appointment.isCompleted
                    ? 'bg-green-500'
                    : appointment.status === 'cancelled'
                      ? 'bg-red-500'
                      : appointment.isToday
                        ? 'bg-blue-500'
                        : 'bg-brand-primary'
                }`}>
                  {appointment.isCompleted ? (
                    <CheckCircle className="h-5 w-5" />
                  ) : appointment.isToday ? (
                    <CalendarClock className="h-5 w-5" />
                  ) : (
                    <Calendar className="h-5 w-5" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">{appointment.displayLabel}</h3>
                  <p className="text-xs text-gray-500">{appointment.primaryServiceName || t('orderDetails.noServices')}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Badge className={`border-0 ${
                  appointment.isCompleted
                    ? 'bg-green-100 text-green-700'
                    : appointment.status === 'cancelled'
                      ? 'bg-red-100 text-red-700'
                      : appointment.isToday
                        ? 'bg-blue-100 text-blue-700'
                        : 'bg-yellow-100 text-yellow-700'
                }`}>
                  {translateAppointmentStatus(t, appointment.statusDisplay || appointment.status)}
                </Badge>
                {appointment.isOverdue && (
                  <Badge className="bg-red-600 text-white border-0">{t('status.overdue')}</Badge>
                )}
                {appointment.isToday && !appointment.isCompleted && (
                  <Badge className="bg-blue-600 text-white border-0">{t('status.today')}</Badge>
                )}
              </div>
            </div>

            <div className="p-4 sm:p-5">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-4">
                {appointment.scheduledStart && (
                  <div className="flex items-start gap-3">
                    <Clock className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500">{t('orderDetails.scheduled')}</p>
                      <p className="font-medium text-gray-900 text-sm truncate">{formatOrderDateTime(appointment.scheduledStart, locale)}</p>
                    </div>
                  </div>
                )}
                {appointment.durationMinutes && (
                  <div className="flex items-start gap-3">
                    <Timer className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div>
                      <p className="text-xs text-gray-500">{t('orderDetails.duration')}</p>
                      <p className="font-medium text-gray-900 text-sm">{t('orderDetails.durationMinutes', { minutes: appointment.durationMinutes })}</p>
                    </div>
                  </div>
                )}
                {appointment.primaryProviderName && (
                  <div className="flex items-start gap-3">
                    <User className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500">{t('orderDetails.provider')}</p>
                      <p className="font-medium text-gray-900 text-sm truncate">{appointment.primaryProviderName}</p>
                    </div>
                  </div>
                )}
                {(appointment.locationAddress || appointment.locationCity) && (
                  <div className="flex items-start gap-3">
                    <MapPin className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <div className="min-w-0">
                      <p className="text-xs text-gray-500 mb-0.5">{t('orderDetails.location')}</p>
                      <GeocodedLocationLink
                        location={formatLocationLabel(
                          appointment.locationAddress,
                          appointment.locationCity
                        )}
                        href={appointment.locationGoogleMapsLink}
                        className="text-sm font-medium"
                        wrap
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Services in this appointment */}
              {appointment.services.length > 0 && (
                <div className="border-t border-gray-100 pt-4">
                  <p className="text-xs font-medium text-gray-500 mb-3">
                    {t('orderDetails.servicesInAppointment', { completed: appointment.completedServicesCount, total: appointment.servicesCount })}
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {appointment.services.map((service) => (
                      <span
                        key={service.id}
                        className={`inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-sm ${
                          service.status === 'completed'
                            ? 'bg-green-50 text-green-700 border border-green-200'
                            : 'bg-gray-50 text-gray-700 border border-gray-200'
                        }`}
                      >
                        {service.status === 'completed' && <CheckCircle className="h-3.5 w-3.5" />}
                        {service.serviceName}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Actions */}
              {(appointment.canComplete) && (
                <div className="flex flex-wrap gap-2 border-t border-gray-100 pt-4 mt-4">
                  {/* {appointment.canStart && (
                    <Button size="sm" className="gap-2 bg-green-600 hover:bg-green-700">
                      <Play className="h-3 w-3" />
                      {t('orderDetails.start')}
                    </Button>
                  )} */}
                  {appointment.canComplete && (
                    <Button size="sm" className="gap-2 bg-blue-600 hover:bg-blue-700">
                      <CheckCircle className="h-3 w-3" />
                      {t('orderDetails.complete')}
                    </Button>
                  )}
                  {/* {appointment.canReschedule && (
                    <Button size="sm" variant="outline" className="gap-2">
                      <RotateCcw className="h-3 w-3" />
                      {t('orderDetails.reschedule')}
                    </Button>
                  )} */}
                  {/* {appointment.canCancel && (
                    <Button
                      size="sm"
                      variant="outline"
                      className="gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
                      onClick={() => onCancelAppointment(appointment.id)}
                    >
                      <XCircle className="h-3 w-3" />
                      {t('cancellation.cancelAppointment')}
                    </Button>
                  )} */}
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

// ============================================================================
// Quotation Tab
// ============================================================================

function QuotationTab({
  order,
  locale,
  isBusinessClientMode,
}: {
  order: OrderDetails
  locale: 'en' | 'ar'
  isBusinessClientMode: boolean
}) {
  const { t } = useLocalization()
  const quotation = order.quotation

  if (!quotation) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-center">
        <div className="mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gray-100">
          <Receipt className="h-8 w-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">{t('orderDetails.noQuotation')}</h3>
        <p className="mt-1 text-gray-500">{t('orderDetails.noQuotationYet')}</p>
      </div>
    )
  }

  const quotationStatusLabel =
    quotation.status === 'accepted'
      ? t('orderDetails.quotationStatusAccepted')
      : quotation.status === 'rejected'
        ? t('orderDetails.quotationStatusRejected')
        : t('common.pending')

  const earningsAvailable =
    order.providerEarningsTotal !== null && order.providerEarningsTotal !== undefined

  const currency =
    isBusinessClientMode ? quotation.currency : order.providerEarningsCurrency || quotation.currency

  const grossAmount = isBusinessClientMode ? quotation.subtotal : order.providerEarningsGrossAmount
  const commissionAmount = earningsAvailable
    ? order.providerEarningsCommission ?? '0'
    : null
  const netAmount = isBusinessClientMode ? quotation.subtotal : order.providerEarningsNet
  const taxAmount = isBusinessClientMode ? quotation.taxAmount : order.providerEarningsTax
  const totalAmount = isBusinessClientMode ? quotation.totalAmount : order.providerEarningsTotal

  return (
    <div className="max-w-2xl mx-auto">
      <div className="rounded-xl border border-gray-200 bg-white overflow-hidden shadow-sm">
        {/* Header */}
        <div className="bg-gradient-to-r from-brand-primary to-brand-secondary px-6 py-5 text-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white/90 text-sm">{t('orderDetails.quotation')}</p>
              <h3 className="text-xl font-bold">#{quotation.quotationNumber}</h3>
            </div>
            <Badge className={`border-0 ${
              quotation.status === 'accepted'
                ? 'bg-green-500 text-white'
                : quotation.status === 'rejected'
                  ? 'bg-red-500 text-white'
                  : 'bg-yellow-500 text-white'
            }`}>
              {quotationStatusLabel}
            </Badge>
          </div>
          {!isBusinessClientMode && earningsAvailable && (
            <p className="mt-2 text-xs text-white/80">
              {t('orderDetails.quotationProviderEarningsView')}
            </p>
          )}
        </div>

        {/* Line Items */}
        <div className="px-6 py-5 border-b border-gray-100">
          <div className="space-y-3">
            {/* <div className="flex justify-between items-center py-2">
              <span className="text-gray-600">
                {isBusinessClientMode ? t('orderDetails.subtotal') : t('orderDetails.grossServiceFee')}
              </span>
              <span className="font-medium text-gray-900">
                {grossAmount !== null
                  ? formatOrderAmount(grossAmount, currency, locale)
                  : t('common.noData')}
              </span>
            </div> */}
            {!isBusinessClientMode && (
              <>
                {/* <div className="flex justify-between items-center py-2">
                  <span className="text-gray-600">
                    {t('orderDetails.platformCommission')}
                  </span>
                  <span className="font-medium text-red-600">
                    {commissionAmount !== null
                      ? `-${formatOrderAmount(commissionAmount, currency, locale)}`
                      : t('common.noData')}
                  </span>
                </div> */}
                <div className="flex justify-between items-center py-2 border-t border-dashed border-gray-100 pt-3">
                  <span className="text-gray-600">
                    {t('orderDetails.netServiceFee')}
                  </span>
                  <span className="font-medium text-gray-900">
                    {netAmount !== null
                      ? formatOrderAmount(netAmount, currency, locale)
                      : t('common.noData')}
                  </span>
                </div>
              </>
            )}
            <div className="flex justify-between items-center py-2">
              <span className="text-gray-600">
                {isBusinessClientMode ? t('orderDetails.vat') : t('orderDetails.taxVat')}
              </span>
              <span className="font-medium text-gray-900">
                {taxAmount !== null
                  ? formatOrderAmount(taxAmount, currency, locale)
                  : t('common.noData')}
              </span>
            </div>
          </div>
        </div>

        {/* Total */}
        <div className="px-6 py-5 bg-gray-50">
          <div className="flex justify-between items-center">
            <span className="text-lg font-semibold text-gray-900">
              {isBusinessClientMode
                ? t('orderDetails.totalAmount')
                : t('orderDetails.providerEarningsTotal')}
            </span>
            <span className="text-2xl font-bold text-brand-primary">
              {totalAmount !== null
                ? formatOrderAmount(totalAmount, currency, locale)
                : t('common.noData')}
            </span>
          </div>
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-gray-100 bg-white">
          <div className="grid grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-gray-500">{t('orderDetails.created')}</p>
              <p className="font-medium text-gray-900">{formatOrderDate(quotation.createdAt, locale)}</p>
            </div>
            {quotation.validUntil && (
              <div>
                <p className="text-gray-500">{t('orderDetails.validUntil')}</p>
                <p className="font-medium text-gray-900">{formatOrderDate(quotation.validUntil, locale)}</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}

// ============================================================================
// Shared Components
// ============================================================================

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-center py-1">
      <span className="text-sm text-gray-500">{label}</span>
      <span className="text-sm font-medium text-gray-900">{value}</span>
    </div>
  )
}

function TimelineItem({
  label,
  date,
  locale,
  isCompleted,
  isOverdue,
  isFirst,
  isLast,
}: {
  label: string
  date: string | number | null
  locale?: 'en' | 'ar'
  isCompleted?: boolean
  isOverdue?: boolean
  isFirst?: boolean
  isLast?: boolean
}) {
  return (
    <div className="flex items-start gap-3 relative">
      {!isLast && (
        <div className="absolute start-[9px] top-5 w-0.5 h-full bg-gray-200" />
      )}
      <div className={`relative z-10 flex h-5 w-5 items-center justify-center rounded-full ${
        isOverdue
          ? 'bg-red-500'
          : isCompleted
            ? 'bg-green-500'
            : 'bg-gray-300'
      }`}>
        {isCompleted && <CheckCircle className="h-3 w-3 text-white" />}
        {isOverdue && <AlertCircle className="h-3 w-3 text-white" />}
        {!isCompleted && !isOverdue && <CircleDot className="h-3 w-3 text-white" />}
      </div>
      <div className="flex-1 pb-4">
        <p className={`text-sm font-medium ${isOverdue ? 'text-red-600' : 'text-gray-900'}`}>{label}</p>
        {date && (
          <p className="text-xs text-gray-500">{formatOrderDateTime(date, locale || 'en')}</p>
        )}
      </div>
    </div>
  )
}

export default OrderDetailsPage
